import { IGameState } from "State/Game/IGameState";
import { IPlayerExpireableQuestData } from "State/Player/IPlayerState";
import { ArrayHelper } from "Utility/ArrayHelper";
import { EventType } from "Utility/Helpers/Events/EventType";
import { IEventStateData } from "./IEventsState";
import { EventLayoutMap } from 'Constants/Events/EventLayoutMap';
import DateHelper from "Utility/DateHelper";
import { getHighestLevelCompleted } from "State/Player/PlayerLevelHelper";
import { isUndefined } from "Utility/Types";

export namespace EventStateHelper {


    /**
     * Get events the have started before 'now' and will complete after 'now'. 'now' is determined using the Datehelper
     * and will also take into account server time offsets.
     * @param state
     */
    export function getActiveEvents(state: IGameState) {
        const now = DateHelper.now;

        return state.eventsState.events.filter(event => {
            const startDate = DateHelper.getDate(event.startTime);
            const isBeforeDurationEnds = DateHelper.timeSince(startDate) <= event.duration;
            const isAfterEventStarts = DateHelper.getTimeUntil(now, startDate) <= 0;
            const meetsLvlRequirement = isUndefined(event.requiredLevel) ? true : getHighestLevelCompleted(state.playerState) >= event.requiredLevel;
            const notAtRequiredLevel = state.levelState.levelNumber !== event.requiredLevel;

            return isAfterEventStarts &&
                isBeforeDurationEnds &&
                meetsLvlRequirement &&
                notAtRequiredLevel;
        })
    }
    /**
     * Gets the questId for the event with the highest prio
     * @param state Game State
     * @returns QuestId of the event with the highest prio quest or undefined if none are found
     */
    export function getActiveQuestId(state: IGameState): string | undefined {
        return getActiveQuest(state)?.id;
    }

    function getEventsWithQuests(state: IGameState): IEventStateData[] {
        return getActiveEvents(state).filter(event => event.hasQuests);
    }

    export function getEventState(state: IGameState, eventType: EventType): IEventStateData {
        return getActiveEvents(state).find(event => event.type === eventType);
    }

    export function getActiveQuest(state: IGameState): IPlayerExpireableQuestData {
        // Active events with quests
        const eventsWithQuests = getEventsWithQuests(state);
        // Highest prio quest
        const eventWithActiveQuest = ArrayHelper.sortByHighestPriority(eventsWithQuests)[0];
        if (eventWithActiveQuest) {
            return state.playerState.eventQuests.find(quest => quest.eventType === eventWithActiveQuest.type);
        }
    }

    export function getEvent(state: IGameState, key: keyof typeof EventLayoutMap[EventType]) {
        return ArrayHelper.sortByHighestPriority(getActiveEvents(state)).find(
            event => EventLayoutMap[event.type]?.[key]
        );
    }

    export function getActiveEvent(state: IGameState): IEventStateData | undefined {
        return ArrayHelper.sortByHighestPriority(getActiveEvents(state))[0];
    }

    /**
     * TODO: Refactor so this is not required to determine which assets are required for each event/UI/scene etc.
     * @param event
     */
    export function isXmasEvent(event: IEventStateData) {
        return event?.type === EventType.XMAS_2021;
    }

    export function getEventLayout(state: IGameState, key: keyof typeof EventLayoutMap[EventType]) {
        const event = getEvent(state, key);

        if (!event) return null;

        if (EventLayoutMap[event.type] && EventLayoutMap[event.type][key]) {
            return new EventLayoutMap[event.type][key]();
        }

        return null;
    }
}
